// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import type { Advisor } from '@shared/data-access/types/brokerage-types';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { getInitials } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import styles from './silver-alpha-home.module.css';

export interface SilverAlphaAdvisorCardProps {
  advisor: Advisor;
}

/** Advisor card: name, email, phone from advisory users API (letter avatar only). */
export const SilverAlphaAdvisorCard: FC<SilverAlphaAdvisorCardProps> = ({
  advisor,
}) => {
  const displayName = advisor.name?.trim() || advisor.email;
  const contactHref = useMemo(() => {
    if (advisor.email) {
      return `mailto:${advisor.email}`;
    }

    if (advisor.phone_number) {
      return `tel:${advisor.phone_number.replace(/\s/g, '')}`;
    }

    return null;
  }, [advisor.email, advisor.phone_number]);

  return (
    <article className={styles.card} data-testid="silver-alpha-advisor-card">
      <p className={styles.cardEyebrow}>
        <Translation
          i18nKey="silver-alpha-home.advisor-card.label"
          ns="components"
        />
      </p>

      <div className={styles.advisorTop}>
        <div className={styles.avatar} aria-hidden>
          {getInitials(displayName)}
        </div>
        <div>
          <h2 className={styles.advisorName}>{displayName}</h2>
          <p className={styles.advisorEntity}>
            <Translation
              i18nKey="advisory-proposal.issuer-entity"
              ns="components"
            />
          </p>
        </div>
      </div>

      <div className={styles.contactList}>
        {advisor.email ? (
          <p className={styles.contactItem}>
            <AppIcon icon="Mail" size="sm" className={styles.contactIcon} />
            <a href={`mailto:${advisor.email}`}>{advisor.email}</a>
          </p>
        ) : null}
        {advisor.phone_number ? (
          <p className={styles.contactItem}>
            <AppIcon icon="Smartphone" size="sm" className={styles.contactIcon} />
            <a href={`tel:${advisor.phone_number.replace(/\s/g, '')}`}>
              {advisor.phone_number}
            </a>
          </p>
        ) : null}
      </div>

      {contactHref ? (
        <div className={styles.advisorFooter}>
          <Button
            color="secondary"
            data-testid="silver-alpha-advisor-contact"
            fullWidth
            href={contactHref}
            size="large"
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            <Translation
              i18nKey="silver-alpha-home.advisor-card.contact"
              ns="components"
            />
          </Button>
        </div>
      ) : null}
    </article>
  );
};
